import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ProjectSetupPage from './pages/ProjectSetupPage';
import GoalsBuilderPage from './pages/GoalsBuilderPage';
import AuditDashboardPage from './pages/AuditDashboardPage';
import AuditDetailPage from './pages/AuditDetailPage';
import HistoryPage from './pages/HistoryPage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SharedReportPage from './pages/SharedReportPage';
import Footer from './components/Footer';
import { Loader2 } from 'lucide-react';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#0268C4] animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#0268C4] animate-spin" />
    </div>
  );
  if (!user || user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#0268C4] animate-spin" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="flex flex-col min-h-screen">
          <div className="flex-1">
            <Routes>
              {/* Public routes - anyone can access */}
              <Route path="/" element={<HomePage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/report/:token" element={<SharedReportPage />} />

              {/* Auth routes - redirect if logged in */}
              <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Protected routes */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/projects/new" element={<ProtectedRoute><ProjectSetupPage /></ProtectedRoute>} />
              <Route path="/projects/:projectId/goals" element={<ProtectedRoute><GoalsBuilderPage /></ProtectedRoute>} />
              <Route path="/projects/:projectId" element={<ProtectedRoute><AuditDashboardPage /></ProtectedRoute>} />
              <Route path="/projects/:projectId/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
              <Route path="/issues/:issueId" element={<ProtectedRoute><AuditDetailPage /></ProtectedRoute>} />

              {/* Admin route */}
              <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
